<template>
  <span>
    <!-- Boton de la cabecera: muestra el ESTADO de la caja y abre el modal. -->
    <button
      type="button"
      class="btn btn-sm pos-cash-btn pos-tap"
      :class="isOpen ? 'pos-cash-btn-open' : 'pos-cash-btn-closed'"
      :disabled="!store.branchId"
      @click="openModal"
    >
      <span class="pos-cash-dot"></span>
      <i class="fa-solid fa-cash-register" aria-hidden="true"></i>
      <span>{{ $t('pos.lbl_cash') }}: {{ isOpen ? $t('pos.lbl_open_short') : $t('pos.lbl_closed_short') }}</span>
    </button>

    <PosModal v-model="show" :title="$t('pos.lbl_cash')" :done-label="$t('pos.lbl_done')" :close-label="$t('pos.lbl_cancel')">
      <AlertMessage class="mb-3" :message="error" />

      <!-- Cierre recien hecho -> Z-report (esperado vs contado vs diferencia) -->
      <template v-if="closeResult">
        <div class="pos-cash-state pos-cash-state-closed mb-3">
          <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
          <div><strong>{{ $t('pos.lbl_cash_closed_done') }}</strong></div>
        </div>
        <div class="pos-cash-summary">
          <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_expected_amount') }}</span><strong>{{ money(closeResult.expected_amount) }}</strong></div>
          <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_counted_amount') }}</span><strong>{{ money(closeResult.closing_amount) }}</strong></div>
          <div class="d-flex justify-content-between pt-1 border-top">
            <span>{{ $t('pos.lbl_difference') }}</span>
            <strong :class="closeResult.difference < 0 ? 'text-danger' : 'text-success'">{{ money(closeResult.difference) }}</strong>
          </div>
        </div>
      </template>

      <!-- Sin caja abierta -> abrirla (primero) -->
      <template v-else-if="!register">
        <div class="pos-cash-state pos-cash-state-closed mb-3">
          <i class="fa-solid fa-lock" aria-hidden="true"></i>
          <div>
            <strong>{{ $t('pos.lbl_cash_closed_title') }}</strong>
            <p class="mb-0">{{ $t('pos.lbl_cash_closed_help') }}</p>
          </div>
        </div>

        <label class="form-label small mb-1" for="pos-cash-open">{{ $t('pos.lbl_opening_amount') }}</label>
        <div class="input-group input-group-sm mb-2">
          <span class="input-group-text">{{ currencySymbol }}</span>
          <input id="pos-cash-open" type="number" min="0" step="0.01" class="form-control" placeholder="0.00" v-model="openingAmount" />
        </div>
        <button type="button" class="btn btn-success w-100 pos-tap" :disabled="busy" @click="openRegister">
          <span v-if="busy" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          <i class="fa-solid fa-lock-open me-1" aria-hidden="true"></i>{{ $t('pos.lbl_open_cash') }}
        </button>
      </template>

      <!-- Caja abierta -> resumen + ingreso/egreso -->
      <template v-else>
        <div class="pos-cash-state pos-cash-state-open mb-3">
          <i class="fa-solid fa-lock-open" aria-hidden="true"></i>
          <div><strong>{{ $t('pos.lbl_cash_open_title') }}</strong></div>
        </div>

        <div class="pos-cash-summary mb-3">
          <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_opening_amount') }}</span><strong>{{ money(register.opening_amount) }}</strong></div>
          <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_expected_amount') }}</span><strong class="text-primary">{{ money(expected) }}</strong></div>
        </div>

        <h6 class="mb-2">{{ $t('pos.lbl_cash_movements') }}</h6>
        <label class="form-label small mb-1" for="pos-cash-mov">{{ $t('pos.lbl_movement_amount') }}</label>
        <div class="input-group input-group-sm mb-2">
          <span class="input-group-text">{{ currencySymbol }}</span>
          <input id="pos-cash-mov" type="number" min="0" step="0.01" class="form-control" placeholder="0.00" v-model="movAmount" />
        </div>
        <input type="text" class="form-control form-control-sm mb-2" :placeholder="$t('pos.lbl_movement_note')" v-model="movNote" />

        <div class="d-flex gap-2">
          <button type="button" class="btn btn-success w-100 pos-tap" :disabled="busy || !(Number(movAmount) > 0)" @click="registerMovement('income')">
            <i class="fa-solid fa-arrow-down me-1" aria-hidden="true"></i>{{ $t('pos.lbl_cash_in') }}
          </button>
          <button type="button" class="btn btn-danger w-100 pos-tap" :disabled="busy || !(Number(movAmount) > 0)" @click="registerMovement('expense')">
            <i class="fa-solid fa-arrow-up me-1" aria-hidden="true"></i>{{ $t('pos.lbl_cash_out') }}
          </button>
        </div>

        <!--
          Movimientos registrados, cada uno con su recibo. Antes el panel solo
          mostraba el total esperado: un ingreso o egreso es plata que entra o
          sale del cajon SIN factura, y no habia forma de emitir constancia.
        -->
        <template v-if="movements.length">
          <hr />
          <h6 class="mb-2">{{ $t('pos.lbl_cash_movements_list') }}</h6>
          <div class="pos-cash-movs">
            <div v-for="m in movementsNewestFirst" :key="m.id" class="d-flex align-items-center gap-2 py-1 border-bottom">
              <div class="flex-grow-1 text-truncate">
                <span class="badge" :class="movBadge(m.type)">{{ movLabel(m.type) }}</span>
                <span class="small text-muted ms-1 text-truncate">{{ m.reference || m.note || '' }}</span>
              </div>
              <strong class="small" :class="m.type === 'expense' ? 'text-danger' : 'text-success'">
                {{ m.type === 'expense' ? '−' : '+' }} {{ money(m.amount) }}
              </strong>
              <a :href="receiptUrl(m.id)" target="_blank" rel="noopener"
                 class="btn btn-sm btn-outline-secondary" :title="$t('pos.lbl_cash_receipt')">
                <i class="fa-solid fa-receipt" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </template>

        <hr />

        <!-- Cierre de caja con ARQUEO: efectivo contado vs esperado -> diferencia. -->
        <button type="button" class="btn btn-outline-dark w-100 pos-tap" v-if="!closing" @click="closing = true">
          <i class="fa-solid fa-lock me-1" aria-hidden="true"></i>{{ $t('pos.lbl_close_cash') }}
        </button>

        <template v-else>
          <h6 class="mb-2">{{ $t('pos.lbl_cash_count') }}</h6>
          <div class="pos-cash-summary mb-2">
            <div class="d-flex justify-content-between"><span class="text-muted">{{ $t('pos.lbl_opening_amount') }}</span><span>{{ money(register.opening_amount) }}</span></div>
            <div class="d-flex justify-content-between"><span class="text-muted">+ {{ $t('pos.lbl_cash_sales') }}</span><span>{{ money(sums.sale) }}</span></div>
            <div class="d-flex justify-content-between"><span class="text-muted">+ {{ $t('pos.lbl_cash_in') }}</span><span>{{ money(sums.income) }}</span></div>
            <div class="d-flex justify-content-between"><span class="text-muted">− {{ $t('pos.lbl_cash_out') }}</span><span>{{ money(sums.expense) }}</span></div>
            <div class="d-flex justify-content-between pt-1 border-top"><strong>{{ $t('pos.lbl_expected_amount') }}</strong><strong class="text-primary">{{ money(expected) }}</strong></div>
          </div>

          <label class="form-label small mb-1" for="pos-cash-count">{{ $t('pos.lbl_counted_amount') }}</label>
          <div class="input-group input-group-sm mb-2">
            <span class="input-group-text">{{ currencySymbol }}</span>
            <input id="pos-cash-count" type="number" min="0" step="0.01" class="form-control" placeholder="0.00" v-model="countedAmount" />
          </div>

          <div class="pos-change mb-2" :class="countedDifference < 0 ? 'pos-change-short' : 'pos-change-ok'" v-if="Number(countedAmount) > 0">
            <span>{{ $t('pos.lbl_difference') }}</span>
            <strong>{{ money(countedDifference) }}</strong>
          </div>

          <div class="d-flex gap-2">
            <button type="button" class="btn btn-secondary pos-tap" @click="closing = false">{{ $t('pos.lbl_cancel') }}</button>
            <button type="button" class="btn btn-dark w-100 pos-tap" :disabled="busy || !(Number(countedAmount) >= 0)" @click="closeRegister">
              <span v-if="busy" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>{{ $t('pos.lbl_confirm_close') }}
            </button>
          </div>
        </template>
      </template>
    </PosModal>
  </span>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AlertMessage from './AlertMessage.vue'
import PosModal from './PosModal.vue'
import { usePos } from '../../store/pos'
import { posRequest } from '../usePosRequest'
import { POS_CASH_CURRENT, POS_CASH_OPEN, POS_CASH_MOVEMENT, POS_CASH_CLOSE, POS_CASH_RECEIPT_URL } from '../constants/pos'
import { money } from '../money'

const store = usePos()
const { t } = useI18n()
const emit = defineEmits(['refresh'])

const show = ref(false)
const busy = ref(false)
const error = ref('')

const register = ref(null)
const expected = ref(0)
const movements = ref([])

// Estado para el boton de la cabecera: la caja consultada o la del catalogo.
const isOpen = computed(() => !!register.value || !!store.catalog.cash_register)

const openingAmount = ref(null)
const movAmount = ref(null)
const movNote = ref('')

// --- Arqueo / cierre ---
const closing = ref(false)
const countedAmount = ref(null)
const closeResult = ref(null)

/** Desglose del efectivo esperado a partir de los movimientos de la caja. */
const sums = computed(() => {
  const acc = { sale: 0, income: 0, expense: 0 }
  ;(movements.value || []).forEach((m) => {
    const amount = Number(m.amount) || 0
    if (m.type === 'sale') acc.sale += amount
    else if (m.type === 'income') acc.income += amount
    else if (m.type === 'expense') acc.expense += amount
  })
  return acc
})

/**
 * Movimientos con el mas reciente arriba. El backend los devuelve en orden de
 * PK y sin ordenar explicitamente, asi que el orden se impone aqui.
 */
const movementsNewestFirst = computed(() => [...(movements.value || [])].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0)))

const receiptUrl = (movementId) => POS_CASH_RECEIPT_URL(movementId)

const movLabel = (type) => t(`pos.lbl_cash_type_${type}`)

const movBadge = (type) => {
  if (type === 'expense') return 'bg-danger'
  if (type === 'income') return 'bg-success'
  if (type === 'sale') return 'bg-primary'
  return 'bg-secondary'
}

/** Diferencia del arqueo = contado - esperado. */
const countedDifference = computed(() => Math.round(((Number(countedAmount.value) || 0) - expected.value) * 100) / 100)

const currencySymbol = (typeof window !== 'undefined' && window.defaultCurrencySymbol) || '$'

const openModal = async () => {
  error.value = ''
  closing.value = false
  closeResult.value = null
  show.value = true
  await loadCurrent()
}

const loadCurrent = async () => {
  if (!store.branchId) return

  const res = await posRequest(POS_CASH_CURRENT({ branch_id: store.branchId }))

  // 404 = no hay caja abierta hoy (estado normal, no error).
  if (res.httpStatus === 404) {
    register.value = null
    expected.value = 0
    movements.value = []
    return
  }

  if (res.ok && res.data) {
    register.value = res.data.cash_register || null
    expected.value = Number(res.data.expected_amount) || 0
    movements.value = Array.isArray(res.data.movements) ? res.data.movements : []
  }
}

const closeRegister = async () => {
  if (!register.value) return

  busy.value = true
  error.value = ''

  const res = await posRequest(POS_CASH_CLOSE(register.value.id), {
    closing_amount: Number(countedAmount.value) || 0
  })

  busy.value = false

  if (!res.ok) {
    error.value = res.message || t('pos.lbl_unexpected_error')
    return
  }

  // Z-report: esperado vs contado vs diferencia. La caja queda cerrada.
  closeResult.value = res.data
  register.value = null
  expected.value = 0
  movements.value = []
  closing.value = false
  countedAmount.value = null
  emit('refresh')
}

const openRegister = async () => {
  busy.value = true
  error.value = ''

  const res = await posRequest(POS_CASH_OPEN(), {
    branch_id: store.branchId,
    opening_amount: Number(openingAmount.value) || 0
  })

  busy.value = false

  if (!res.ok) {
    error.value = res.message || t('pos.lbl_unexpected_error')
    return
  }

  openingAmount.value = null
  await loadCurrent()
  emit('refresh')
}

const registerMovement = async (type) => {
  if (!register.value || !(Number(movAmount.value) > 0)) return

  busy.value = true
  error.value = ''

  const res = await posRequest(POS_CASH_MOVEMENT(register.value.id), {
    type,
    amount: Number(movAmount.value),
    note: movNote.value.trim() || null
  })

  busy.value = false

  if (!res.ok) {
    error.value = res.message || t('pos.lbl_unexpected_error')
    return
  }

  movAmount.value = null
  movNote.value = ''
  await loadCurrent()
  emit('refresh')
}
</script>
